import { useProfile } from '@/hooks/profile';
import { hasPermission, ScopedPermission } from '@/utils/authorize';
import { useMemo } from 'react';

import {
  OrganizationFieldsFragment,
  ProfileRolesFragment,
  RoleScope,
} from '@/generated/graphql';

type Profile = ProfileRolesFragment & {
  organization?: OrganizationFieldsFragment | null;
};

type UseAuthorizeResult = readonly [boolean, { loading: boolean }];

export type Permission = ScopedPermission | string;

interface AuthorizeOptions {
  resourceId?: string | null;
  profileOverride?: Profile | null;
}

// determine if any of the requested permissions is authorized

export function useAuthorize(
  permissions: readonly Permission[],
  { resourceId, profileOverride }: AuthorizeOptions = {},
): UseAuthorizeResult {
  const { profile, loading } = useProfile({ skip: Boolean(profileOverride) });
  const authProfile = profileOverride ?? profile;

  const allowed = useMemo(() => {
    const perms = permissions.map((perm) =>
      typeof perm === 'string'
        ? [perm]
        : {
            ...perm,
            resourceId:
              perm.scope !== RoleScope.Root && !perm.resourceId
                ? resourceId ??
                  (perm.scope === RoleScope.Organization
                    ? authProfile?.organization?.id
                    : perm.resourceId)
                : perm.resourceId,
          },
    );

    return (
      !!authProfile?.roles &&
      perms.some((perm) => hasPermission(authProfile.roles, perm))
    );
  }, [authProfile, permissions, resourceId]);

  return [allowed, { loading }] as const;
}

const assignRolePermission = ['profiles:full', 'roles:full'];

export function useAuthorizeAssignRole(
  targetProfile?: Profile | null,
  profileOverride?: Profile | null,
): UseAuthorizeResult {
  const { profile, loading } = useProfile({ skip: Boolean(profileOverride) });
  const authProfile = profileOverride ?? profile;

  const [canAssign] = useAuthorize(assignRolePermission, { profileOverride });

  const allowed =
    canAssign ||
    (authProfile?.roles ?? []).some(
      (role) =>
        (role.scope === RoleScope.Marketplace &&
          role.permissions.includes('marketplace.roles:full')) ||
        (role.scope === RoleScope.Organization &&
          role.resourceId === targetProfile?.organization?.id &&
          ['organization.roles:full', 'organization.profiles:full'].some(
            (perm) => role.permissions.includes(perm),
          )),
    );

  return [allowed, { loading }] as const;
}
